import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StatsModal from './StatsModal';

export default function Investment() {
  const navigate = useNavigate();
  const [selectedModal, setSelectedModal] = useState<'market-cap' | 'volume' | 'holders' | 'staking' | null>(null);
  const [selectedInvestment, setSelectedInvestment] = useState<number>(1);

  const stats = [
    {
      icon: 'ri-funds-line',
      label: 'Market Cap',
      value: '$2.5B',
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: 'ri-line-chart-line',
      label: '24h Volume',
      value: '$450M',
      color: 'from-cyan-500 to-blue-500'
    },
    {
      icon: 'ri-group-line',
      label: 'Total Holders',
      value: '125K+',
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: 'ri-coins-line',
      label: 'Staking APY',
      value: '25%',
      color: 'from-orange-500 to-red-500'
    }
  ];

  const investmentOptions = [
    {
      title: 'Conservative',
      subtitle: 'Low Risk',
      returns: '8-12%',
      period: '12 months',
      description: 'Stable returns with minimal risk through diversified portfolio',
      features: ['Guaranteed returns', 'Low volatility', 'Flexible withdrawal'],
      color: 'from-green-500/20 to-emerald-500/20',
      borderColor: 'border-green-500/50',
      icon: 'ri-shield-check-line',
      apy: '12%',
      popular: false
    },
    {
      title: 'Balanced',
      subtitle: 'Medium Risk',
      returns: '35-50%',
      period: '6-12 months',
      description: 'Optimal balance between risk and reward for steady growth',
      features: ['Balanced portfolio', 'Regular dividends', 'Professional management'],
      color: 'from-blue-500/20 to-cyan-500/20',
      borderColor: 'border-blue-500/50',
      icon: 'ri-scales-line',
      apy: '45%',
      popular: true
    },
    {
      title: 'Aggressive',
      subtitle: 'High Risk',
      returns: '80-150%',
      period: '3-6 months',
      description: 'Maximum growth potential with higher risk tolerance',
      features: ['High growth potential', 'Active trading', 'Premium strategies'],
      color: 'from-purple-500/20 to-pink-500/20',
      borderColor: 'border-purple-500/50',
      icon: 'ri-rocket-line',
      apy: '120%',
      popular: false
    }
  ];

  const handleStatsClick = (type: 'market-cap' | 'volume' | 'holders' | 'staking') => {
    setSelectedModal(type);
  };

  const handleInvestmentSelect = (index: number) => {
    setSelectedInvestment(index);
  };

  const handleStartInvesting = () => {
    navigate('/investment');
  };

  return (
    <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-96 h-96 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <div className="inline-flex items-center gap-1.5 sm:gap-2 bg-gradient-to-r from-purple-100 to-pink-100 px-3 py-1 sm:px-4 sm:py-1.5 rounded-full mb-3 sm:mb-4">
            <i className="ri-funds-line text-purple-600 text-xs sm:text-sm"></i>
            <span className="text-purple-600 font-semibold text-[10px] sm:text-xs">Smart Investment</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2 sm:mb-3">
            Grow Your Wealth with <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">PepCoin</span>
          </h2>
          <p className="text-xs sm:text-sm md:text-base text-gray-600 max-w-2xl mx-auto">
            Multiple investment strategies designed to maximize your returns
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-8 sm:mb-12">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
            >
              <div className={`w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center mb-2 sm:mb-3`}>
                <i className={`${stat.icon} text-white text-xs sm:text-sm md:text-base`}></i>
              </div>
              <div className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-0.5 sm:mb-1">{stat.value}</div>
              <div className="text-[10px] sm:text-xs text-gray-600">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Investment Options */}
        <div className="grid md:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-12">
          {investmentOptions.map((option, index) => (
            <div
              key={index}
              className={`relative bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 ${
                option.popular ? 'border-purple-500' : 'border-gray-100'
              } group cursor-pointer`}
            >
              {option.popular && (
                <div className="absolute -top-2.5 sm:-top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-3 py-0.5 sm:px-4 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold shadow-lg">
                    MOST POPULAR
                  </span>
                </div>
              )}

              <div className={`w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-br ${option.color} flex items-center justify-center mb-3 sm:mb-4 md:mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <i className={`${option.icon} text-white text-base sm:text-lg md:text-2xl`}></i>
              </div>

              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-1.5 sm:mb-2">{option.title}</h3>
              <p className="text-[10px] sm:text-xs md:text-sm text-gray-600 mb-3 sm:mb-4 md:mb-6">{option.description}</p>

              <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                {option.features.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-green-500 text-xs sm:text-sm mt-0.5"></i>
                    <span className="text-[10px] sm:text-xs md:text-sm text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="flex items-baseline gap-1 sm:gap-2 mb-3 sm:mb-4 md:mb-6">
                <span className="text-2xl sm:text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
                  {option.apy}
                </span>
                <span className="text-xs sm:text-sm text-gray-600">APY</span>
              </div>

              <button className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-2 sm:py-2.5 md:py-3 px-4 sm:px-6 rounded-lg sm:rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-300 group-hover:shadow-lg text-xs sm:text-sm md:text-base cursor-pointer whitespace-nowrap">
                Start Investing
              </button>
            </div>
          ))}
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Platform Participation Options
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Multiple engagement methods with varying risk profiles. All options subject to market conditions, smart contract risks, and potential capital loss.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {[
              {
                icon: 'ri-safe-2-line',
                title: 'Token Staking Protocol',
                description: 'Lock tokens to support network security. Rewards variable based on network participation. Minimum lock-up period applies. Early withdrawal penalties may apply.',
                risk: 'Medium-High Risk',
                gradient: 'from-blue-500 to-cyan-500'
              },
              {
                icon: 'ri-exchange-funds-line',
                title: 'Liquidity Pool Provision',
                description: 'Provide paired assets to decentralized exchanges. Exposure to impermanent loss and smart contract vulnerabilities. Fees earned vary with trading volume.',
                risk: 'High Risk',
                gradient: 'from-purple-500 to-pink-500'
              },
              {
                icon: 'ri-robot-line',
                title: 'Automated Trading Strategies',
                description: 'Algorithm-based trading execution. Past performance not indicative of future results. Subject to market volatility, slippage, and execution risks.',
                risk: 'Very High Risk',
                gradient: 'from-emerald-500 to-teal-500'
              }
            ].map((option, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-100">
                <div className={`w-16 h-16 bg-gradient-to-br ${option.gradient} rounded-2xl flex items-center justify-center mb-6`}>
                  <i className={`${option.icon} text-white text-3xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{option.title}</h3>
                <div className="inline-block px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-semibold mb-4">
                  {option.risk}
                </div>
                <p className="text-gray-600 leading-relaxed mb-6">{option.description}</p>
                <button className="w-full px-6 py-3 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300 whitespace-nowrap">
                  View Details & Risks
                </button>
              </div>
            ))}
          </div>

          {/* Risk Disclosure Box - Compact for Mobile */}
          <div className="bg-red-50 border-2 border-red-200 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8">
            <div className="flex items-start gap-2 sm:gap-3 md:gap-4">
              <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-red-500 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line text-white text-base sm:text-xl md:text-2xl"></i>
              </div>
              <div>
                <h3 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold text-red-900 mb-2 sm:mb-3">Risk Disclosure</h3>
                <div className="space-y-1.5 sm:space-y-2 text-red-800 text-xs sm:text-sm">
                  <p className="flex items-start gap-1.5 sm:gap-2">
                    <i className="ri-arrow-right-s-fill mt-0.5 sm:mt-1 flex-shrink-0"></i>
                    <span><strong>Capital Loss:</strong> You may lose all invested capital.</span>
                  </p>
                  <p className="flex items-start gap-1.5 sm:gap-2">
                    <i className="ri-arrow-right-s-fill mt-0.5 sm:mt-1 flex-shrink-0"></i>
                    <span><strong>Smart Contract Risk:</strong> Code vulnerabilities may result in permanent loss.</span>
                  </p>
                  <p className="flex items-start gap-1.5 sm:gap-2">
                    <i className="ri-arrow-right-s-fill mt-0.5 sm:mt-1 flex-shrink-0"></i>
                    <span><strong>Regulatory Uncertainty:</strong> Legal status varies by jurisdiction.</span>
                  </p>
                  <p className="flex items-start gap-1.5 sm:gap-2">
                    <i className="ri-arrow-right-s-fill mt-0.5 sm:mt-1 flex-shrink-0"></i>
                    <span><strong>No Guarantees:</strong> No returns or liquidity guaranteed.</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Modal */}
      <StatsModal
        isOpen={selectedModal !== null}
        onClose={() => setSelectedModal(null)}
        type={selectedModal || 'market-cap'}
      />
    </section>
  );
}
